import React from "react";
import { Switch } from "@/components/ui/switch";
// import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormikSelect, FormikInput } from "../FormikField";
import { Calculator, Info } from "lucide-react";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { Badge } from "../ui/badge";
import { useTranslation } from "react-i18next";

interface CounterFeedingConfigProps {
  formik: FormikProps<SilaeFormData>;
  prefix?: string; // Pour différencier IF/ELSE (legacy, deprecated)
  fieldPrefix?: string; // Nouveau système: chemin complet (ex: "ifBranch.bonuses.0")
  className?: string;
}

/**
 * Composant pour configurer l'alimentation des compteurs (méthode 99)
 */
export const CounterFeedingConfig: React.FC<CounterFeedingConfigProps> = ({
  formik,
  prefix = "",
  fieldPrefix,
  className = "",
}) => {
  const { t } = useTranslation();

  // Nouveau système avec fieldPrefix (structure hiérarchique)
  const enableField = fieldPrefix
    ? `${fieldPrefix}.enableCounterFeeding`
    : prefix
      ? `enable${prefix}CounterFeeding`
      : "enableCounterFeeding";

  const typeField = fieldPrefix
    ? `${fieldPrefix}.counterType`
    : prefix
      ? `${prefix.toLowerCase()}CounterType`
      : "counterType";

  const valueTypeField = fieldPrefix
    ? `${fieldPrefix}.counterValueType`
    : prefix
      ? `${prefix.toLowerCase()}CounterValueType`
      : "counterValueType";

  const customExprField = fieldPrefix
    ? `${fieldPrefix}.counterCustomExpression`
    : prefix
      ? `${prefix.toLowerCase()}CounterCustomExpression`
      : "counterCustomExpression";

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = (path: string) => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return undefined;
    }
    return value;
  };

  const isEnabled = getFieldValue(enableField) as boolean;

  // Options de compteurs disponibles selon le guide SILAE
  const counterTypeOptions = [
    // === CONGÉS PAYÉS ===
    {
      value: "AjouteCPAcquisAnt",
      label: t("counterFeedingConfig.counterTypes.cpAcquisAnt"),
    },
    {
      value: "AjouteCPPrisAnt",
      label: t("counterFeedingConfig.counterTypes.cpPrisAnt"),
    },
    {
      value: "AjouteCpAcquisRef",
      label: t("counterFeedingConfig.counterTypes.cpAcquisRef"),
    },
    {
      value: "AjouteCPPrisRef",
      label: t("counterFeedingConfig.counterTypes.cpPrisRef"),
    },

    // === CONGÉS SUPPLÉMENTAIRES ===
    {
      value: "AffecteCPSupJoursAcquis",
      label: t("counterFeedingConfig.counterTypes.cpSupAcquis"),
    },
    {
      value: "AffecteCPSupJoursPris",
      label: t("counterFeedingConfig.counterTypes.cpSupPris"),
    },
    {
      value: "AffecteCPSup2JoursAcquis",
      label: t("counterFeedingConfig.counterTypes.cpSup2Acquis"),
    },
    {
      value: "AffecteCPSup2JoursPris",
      label: t("counterFeedingConfig.counterTypes.cpSup2Pris"),
    },

    // === RTT ===
    {
      value: "AjouteRTTAcquis",
      label: t("counterFeedingConfig.counterTypes.rttAcquis"),
    },
    {
      value: "AjouteRTTpris",
      label: t("counterFeedingConfig.counterTypes.rttPris"),
    },
  ];

  return (
    <Card className={`bg-background dark:bg-background my-0 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="h-6 w-6 text-muted-foreground" />
            <div className="flex flex-col ">
              <CardTitle className="text-lg">
                {prefix
                  ? t("counterFeedingConfig.titleWithPrefix", { prefix })
                  : t("counterFeedingConfig.title")}
                <Badge variant="outline" className="text-xs ml-2">
                  {t("counterFeedingConfig.optional")}
                </Badge>
              </CardTitle>
              <CardDescription>
                {t("counterFeedingConfig.description")}
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => {
              formik.setFieldValue(enableField, checked);
              setTimeout(() => formik.validateForm(), 0);
            }}
          />
        </div>
      </CardHeader>
      {isEnabled && (
        <CardContent className="space-y-4">
          <div className="space-y-4 pt-4 border-t">
            {/* Sélection du type de compteur */}
            <FormikSelect
              formik={formik}
              name={typeField}
              label={t("counterFeedingConfig.counterType")}
              placeholder={t("counterFeedingConfig.chooseCounter")}
              options={counterTypeOptions}
            />

            {/* Choix de la valeur à alimenter */}
            <FormikSelect
              formik={formik}
              name={valueTypeField}
              label={t("counterFeedingConfig.valueType")}
              placeholder={t("counterFeedingConfig.valueType")}
              options={[
                {
                  value: "bases",
                  label: t("counterFeedingConfig.valueTypes.bases"),
                },
                {
                  value: "tauxs",
                  label: t("counterFeedingConfig.valueTypes.tauxs"),
                },
                {
                  value: "bases_times_tauxs",
                  label: t("counterFeedingConfig.valueTypes.bases_times_tauxs"),
                },
                {
                  value: "custom",
                  label: t("counterFeedingConfig.valueTypes.custom"),
                },
              ]}
            />

            {getFieldValue(valueTypeField) === "custom" && (
              <FormikInput
                formik={formik}
                name={customExprField}
                label={t("counterFeedingConfig.customExpression")}
                placeholder={t(
                  "counterFeedingConfig.customExpressionPlaceholder",
                )}
              />
            )}

            {/* Information sur l'utilisation de la base calculée */}
            <div className="flex items-start gap-2 p-3 bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800 rounded-lg">
              <Info className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-sky-700 dark:text-sky-300">
                <div className="text-xs mt-1">
                  {t("counterFeedingConfig.helpText")}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
